/**
 * Deep-link scroll affordances for any scrollable section inside a slideout
 * (or any host). Two variants, differing in what they key on and how loud
 * they are:
 *
 * `useFocusedAssociationScroll` — ROW granularity, keyed on an entity id
 * (`?focusedAssociationId=<sub-entity-id>` +
 * `data-focused-association-id`). Scrolls the row to centre and briefly
 * applies `focus-highlight` for a fade animation. Pairs with
 * `useNavigateToEntity`'s `SUB_ENTITY_NAV_REGISTRY`.
 *
 * `useFocusedSectionScroll` — SECTION granularity, keyed on a stable section
 * identifier (`?focusedSection=<section>` + `data-focused-section`). Scrolls
 * the section to the top of the scroll container with NO highlight. Used by
 * the dashboard's "View full prep" button to land the meeting slideout on the
 * prep block.
 *
 * Why the section variant is not keyed on an entity id: the dashboard row and
 * the slideout resolve a meeting's prep record through DIFFERENT queries —
 * `pages/dashboard/components/utils/meetingResearchQuery.ts` does not filter
 * on `ahoy_research_type`, while `record/useMeetingPrepBrief.ts` does — so
 * they can legitimately select different records for the same meeting. An
 * entity-id key would silently miss.
 *
 * The section variant treats its param as a one-shot NAVIGATION INSTRUCTION,
 * not view state: it deletes the param as soon as it acts. See
 * `useFocusedSectionScroll` for why, including why it deletes on a miss too.
 *
 * The lookup is `document.querySelector` against the data-attribute, which is
 * more flexible than coupling to a specific component name — a notes section,
 * a research card, a property block, ANY future surface can opt into the
 * pattern by stamping the attribute.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** URL search-param key the host slideout reads. */
export const FOCUSED_ASSOCIATION_ID_PARAM = 'focusedAssociationId';
/** DOM data attribute scrollable rows must set to participate. */
export const FOCUSED_ASSOCIATION_DATA_ATTR = 'data-focused-association-id';
/** CSS class temporarily applied to the focused row. */
export const FOCUSED_HIGHLIGHT_CLASS = 'focus-highlight';
/** How long the highlight class stays applied before being removed. */
const HIGHLIGHT_DURATION_MS = 3000;

/** URL search-param key for section-level focus. */
export const FOCUSED_SECTION_PARAM = 'focusedSection';
/** DOM data attribute a section stamps to become a scroll target. */
export const FOCUSED_SECTION_DATA_ATTR = 'data-focused-section';
/** `?focusedSection` value for a meeting's prep block. */
export const FOCUSED_SECTION_MEETING_PREP = 'meeting_prep';

/**
 * Every transient focus param this module owns.
 *
 * Exported so `SlideoutStackContext` can sweep un-consumed instructions when
 * the slideout stack empties — the backstop for the case where the receiving
 * section never mounts (e.g. the user closes the slideout before the prep
 * query resolves), which would otherwise strand the param in the URL.
 */
export const TRANSIENT_FOCUS_PARAMS = [
  FOCUSED_ASSOCIATION_ID_PARAM,
  FOCUSED_SECTION_PARAM,
] as const;

/**
 * Resolve the element a focus param points at, or null.
 *
 * `CSS.escape` guards against unusual values — UUIDs are safe, but section
 * keys and future custom kinds may not be.
 */
function findFocusTarget(attr: string, value: string): Element | null {
  return document.querySelector(`[${attr}="${CSS.escape(value)}"]`);
}

/**
 * Scroll a resolved focus target into view, honouring `prefers-reduced-motion`
 * (same treatment as `components/dynamic-views/utils/emojiToIcon.tsx`).
 */
function scrollFocusTargetIntoView(
  element: Element,
  block: ScrollLogicalPosition
): void {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  element.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block,
  });
}

/**
 * Scroll to and briefly highlight the row whose
 * `data-focused-association-id` matches the URL's
 * `focusedAssociationId` param.
 *
 * @param itemsLoaded - Pass `true` once your list/section has rendered
 *   its rows. Re-runs the effect when this transitions, so async lists
 *   still trigger the scroll once data arrives.
 *
 * @example
 *   const notes = ...;
 *   useFocusedAssociationScroll(notes.length > 0);
 *   return notes.map(note => (
 *     <div key={note.entity_id} data-focused-association-id={note.entity_id}>
 *       <NoteCard note={note} />
 *     </div>
 *   ));
 */
export function useFocusedAssociationScroll(itemsLoaded: boolean): void {
  const [searchParams] = useSearchParams();
  const focusedId = searchParams.get(FOCUSED_ASSOCIATION_ID_PARAM);

  useEffect(() => {
    if (!focusedId || !itemsLoaded) {
      return;
    }
    const element = findFocusTarget(FOCUSED_ASSOCIATION_DATA_ATTR, focusedId);
    if (!element) {
      return;
    }
    scrollFocusTargetIntoView(element, 'center');
    element.classList.add(FOCUSED_HIGHLIGHT_CLASS);
    const timeoutId = setTimeout(() => {
      element.classList.remove(FOCUSED_HIGHLIGHT_CLASS);
    }, HIGHLIGHT_DURATION_MS);
    return () => {
      clearTimeout(timeoutId);
      element.classList.remove(FOCUSED_HIGHLIGHT_CLASS);
    };
  }, [focusedId, itemsLoaded]);
}

/**
 * Scroll to the section whose `data-focused-section` matches the URL's
 * `focusedSection` param, then CONSUME the param. No highlight — the scroll
 * itself is the affordance.
 *
 * The param is a one-shot navigation instruction, not view state, so it is
 * deleted as soon as this hook acts:
 *
 *  - it must not outlive the navigation (a param that survives the slideout
 *    closing is the bug this whole affordance was rebuilt to fix);
 *  - a persisting param would re-fire the scroll on any later render that
 *    flips `sectionRendered` back to true (a refetch, a remount), yanking the
 *    user back mid-read.
 *
 * It is deleted on a MISS too. If the target never renders — prep absent, or
 * a hand-crafted URL on an entity type with no such section — consuming
 * anyway is what stops the param stranding. `sectionRendered` (not
 * `element !== null`) is therefore the gate: the caller decides when the DOM
 * has settled enough for a miss to be meaningful.
 *
 * `replace: true` is required. `pushSlideout` created the current history
 * entry WITH the param, so clearing with a push would leave a Back step that
 * returns to the with-param URL and immediately re-fires the scroll.
 *
 * @param sectionRendered - `true` once the stamped element is actually in the
 *   DOM (not merely "the query settled"), or once it's certain it never will
 *   be for this render pass.
 */
export function useFocusedSectionScroll(sectionRendered: boolean): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedSection = searchParams.get(FOCUSED_SECTION_PARAM);

  // Mirror the setter so the effect isn't re-run every time the query string
  // changes — react-router recreates `setSearchParams` on each change. Same
  // pattern as SlideoutStackContext's `setSearchParamsRef`.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    if (!focusedSection || !sectionRendered) {
      return;
    }
    const element = findFocusTarget(FOCUSED_SECTION_DATA_ATTR, focusedSection);
    if (element) {
      // 'start' rather than 'center': the target is a section, and we want its
      // top aligned near the top of the scroll container.
      scrollFocusTargetIntoView(element, 'start');
    }
    setSearchParamsRef.current(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(FOCUSED_SECTION_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [focusedSection, sectionRendered]);
}
